<div class="d-flex flex-column h-100"
     [style.pointer-events]="disabled() ? 'none' : null"
     [style.opacity]="disabled() ? '0.6' : null">

  <!-- Header -->
  <div class="panel-section-header px-3 pt-2 pb-2 flex-shrink-0">
    <div class="panel-eyebrow mat-font-label-sm text-uppercase fw-semibold mb-1">
      Custom Elements · Step 2 of 2
    </div>
    <div class="d-flex align-items-center gap-1 mb-1">
      <h2 class="mat-font-title-md fw-semibold mb-0">Configure detection</h2>
    </div>
    <p class="mat-font-body-sm mb-0">
      Detect custom objects in aerial imagery. Choose what to detect and tune the model.
    </p>
  </div>

  <!-- Body -->
  <div class="overflow-y-auto flex-fill px-3 py-3 d-flex flex-column gap-3">

    <!-- Section: Detection area -->
    <div>
      <div class="d-flex align-items-center justify-content-between mb-2">
        <span class="mat-font-label-sm text-uppercase fw-semibold">
          Detection area
        </span>
      </div>
      <lib-area-summary-card
        [area]="detection.area()"
        [showChangeButton]="!disabled()"
        (changeArea)="detection.setStage('draw')" />
    </div>

    <!-- Section: Tile resolution -->
    <div>
      <div class="mat-font-label-sm text-uppercase fw-semibold mb-2">
        Tile resolution
      </div>
      <mat-form-field appearance="fill" subscriptSizing="dynamic" class="form-slim w-100">
        <mat-select
          [value]="detection.tileZoom()"
          [disabled]="disabled()"
          (selectionChange)="detection.setTileZoom($event.value)">
          @for (z of zoomLevels; track z.value) {
            <mat-option [value]="z.value">{{ z.label }}</mat-option>
          }
        </mat-select>
      </mat-form-field>

      <!-- Tile count info -->
      @if (detection.tileCount() > 0) {
        <div class="mat-font-body-sm mt-1">
          @if (detection.tileCount() > maxTiles) {
            <span [style.color]="'var(--mat-sys-error)'">
              Area too large — {{ detection.tileCount() }} tiles (limit: {{ maxTiles }})
            </span>
          } @else if (detection.tileCount() > 1) {
            <span>{{ detection.tileCount() }} tiles</span>
          } @else {
            <span>1 tile — no tiling needed</span>
          }
        </div>
      }
    </div>

    <!-- Section: Search buffer -->
    <div>
      <div class="mat-font-label-sm text-uppercase fw-semibold mb-2">
        Search buffer (meters)
      </div>
      <mat-form-field appearance="fill" subscriptSizing="dynamic" class="form-slim buffer-input w-100">
        <input matInput type="number"
               [min]="0" [max]="maxSearchBuffer" [step]="searchBufferStep"
               [disabled]="disabled()"
               [value]="detection.searchBuffer()"
               (input)="onBufferInput($event)"
               (change)="detection.setSearchBuffer(+$any($event.target).value)" />
      </mat-form-field>
    </div>

    <!-- Section: Objects to detect -->
    <div>
      <div class="mat-font-label-sm text-uppercase fw-semibold mb-2">
        Objects to detect
      </div>

      <lib-object-input
        [count]="detection.objects().length"
        [maxItems]="maxObjects"
        placeholder="e.g. Cars, Trees, Pools…"
        ariaLabel="Add detection object"
        [disabled]="disabled()"
        (itemAdded)="addObject($event)" />

      @if (detection.objects().length > 0) {
        <div class="d-flex flex-column gap-2 mt-2">
          @for (obj of detection.objects(); track obj.name) {
            <lib-object-row
              [object]="obj"
              [disabled]="disabled()"
              (colorChanged)="detection.updateObject(obj.name, { color: $event })"
              (thresholdChanged)="detection.updateObject(obj.name, { threshold: $event })"
              (nameChanged)="detection.renameObject(obj.name, $event)"
              (removed)="detection.removeObject($event)" />
          }
        </div>
      }
    </div>

    <!-- Section: Dev mode (Ctrl+0) -->
    @if (showDevMode()) {
      <div class="d-flex flex-column gap-2">
        <div class="mat-font-label-sm text-uppercase fw-semibold" [style.color]="'var(--mat-sys-outline)'">
          Advanced (Ctrl+0)
        </div>

        <div>
          <div class="mat-font-body-sm mb-1">Tile buffer (% of tile side)</div>
          <mat-form-field appearance="fill" subscriptSizing="dynamic" class="form-slim w-100">
            <input matInput type="number"
                   [min]="0" [step]="0.05"
                   [disabled]="disabled()"
                   [value]="detection.tileBufferPct()"
                   (change)="detection.setTileBufferPct(+$any($event.target).value)" />
          </mat-form-field>
        </div>

        <div>
          <div class="mat-font-body-sm mb-1">Tile zoom offset</div>
          <mat-form-field appearance="fill" subscriptSizing="dynamic" class="form-slim w-100">
            <input matInput type="number"
                   [min]="0"
                   [disabled]="disabled()"
                   [value]="detection.tileZoomOffset()"
                   (change)="detection.setTileZoomOffset(+$any($event.target).value)" />
          </mat-form-field>
        </div>

        <div>
          <div class="mat-font-body-sm mb-1">Merge IoU threshold (0–1)</div>
          <mat-form-field appearance="fill" subscriptSizing="dynamic" class="form-slim w-100">
            <input matInput type="number"
                   [min]="0" [max]="1" [step]="0.05"
                   [disabled]="disabled()"
                   [value]="detection.iouThreshold()"
                   (change)="detection.setIouThreshold(+$any($event.target).value)" />
          </mat-form-field>
        </div>

        <div>
          <div class="mat-font-body-sm mb-1">Tile edge margin (0–0.49)</div>
          <mat-form-field appearance="fill" subscriptSizing="dynamic" class="form-slim w-100">
            <input matInput type="number"
                   [min]="0" [max]="0.49" [step]="0.01"
                   [disabled]="disabled()"
                   [value]="detection.tileEdgeMarginPct()"
                   (change)="detection.setTileEdgeMarginPct(+$any($event.target).value)" />
          </mat-form-field>
        </div>

        <mat-checkbox
          [checked]="detection.debugTilesVisible()"
          [disabled]="disabled()"
          (change)="detection.debugTilesVisible.set($event.checked)">
          Show tile grid on map
        </mat-checkbox>
      </div>
    }

  </div>

  <!-- Footer (hidden while processing) -->
  @if (!disabled()) {
    <div class="px-3 py-2 flex-shrink-0">
      <div class="d-flex gap-2">
        <button matButton class="flex-fill" type="button"
                (click)="detection.setStage('draw')">
          Cancel
        </button>
        <button matButton="filled" color="primary" class="flex-fill" type="button"
                [disabled]="detection.objects().length === 0 || detection.tileCount() > maxTiles"
                (click)="detection.startRun()">
          Run detection
        </button>
      </div>
    </div>
  }

</div>